/*
 * <shoppable-image> — toggles product hotspot popovers over a tagged image.
 * One card open at a time; closes on outside click or Escape. On open it
 * flips the card above the dot when the dot sits low, and clamps the card
 * horizontally so it never spills past the image edge.
 */
if (!customElements.get('shoppable-image')) {
  customElements.define(
    'shoppable-image',
    class ShoppableImage extends HTMLElement {
      constructor() {
        super();
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyup = this.onKeyup.bind(this);
      }

      connectedCallback() {
        this.hotspots = Array.from(this.querySelectorAll('.shoppable-hotspot'));
        this.hotspots.forEach((hotspot) => {
          const button = hotspot.querySelector('.shoppable-hotspot__button');
          if (!button) return;
          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.toggle(hotspot);
          });
        });
        this.addEventListener('keyup', this.onKeyup);
        this.setupReveal();
      }

      /*
       * Desktop (hover-capable) devices reveal dots on hover via CSS, so JS
       * does nothing there. On touch devices the dots fade in once the image
       * scrolls into view.
       */
      setupReveal() {
        const isTouch = window.matchMedia('(hover: none), (pointer: coarse)').matches;
        if (!isTouch) return;

        if (!('IntersectionObserver' in window)) {
          this.classList.add('is-revealed');
          return;
        }

        this.revealObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              this.classList.add('is-revealed');
              this.revealObserver.disconnect();
            });
          },
          { threshold: 0.35 }
        );
        this.revealObserver.observe(this);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        if (this.revealObserver) this.revealObserver.disconnect();
      }

      toggle(hotspot) {
        const isOpen = hotspot.classList.contains('is-open');
        this.closeAll();
        if (!isOpen) this.open(hotspot);
      }

      open(hotspot) {
        hotspot.classList.add('is-open');
        const button = hotspot.querySelector('.shoppable-hotspot__button');
        if (button) button.setAttribute('aria-expanded', 'true');
        this.positionCard(hotspot);
        document.addEventListener('click', this.onDocumentClick);
      }

      closeAll() {
        this.hotspots.forEach((hotspot) => {
          hotspot.classList.remove('is-open');
          const button = hotspot.querySelector('.shoppable-hotspot__button');
          if (button) button.setAttribute('aria-expanded', 'false');
        });
        document.removeEventListener('click', this.onDocumentClick);
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) this.closeAll();
      }

      onKeyup(event) {
        if (event.key === 'Escape') this.closeAll();
      }

      positionCard(hotspot) {
        const card = hotspot.querySelector('.shoppable-hotspot__card');
        if (!card) return;
        card.classList.remove('is-flip-up', 'is-align-left', 'is-align-right');

        const figure = this.getBoundingClientRect();
        const dot = hotspot.getBoundingClientRect();

        // Flip above the dot when it sits in the lower part of the image.
        if (dot.top - figure.top > figure.height * 0.55) {
          card.classList.add('is-flip-up');
        }

        // Measure after the flip class so layout is settled, then clamp
        // horizontally to the figure's edges.
        const rect = card.getBoundingClientRect();
        if (rect.left < figure.left) {
          card.classList.add('is-align-left');
        } else if (rect.right > figure.right) {
          card.classList.add('is-align-right');
        }
      }
    }
  );
}
